import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Modal from "@mui/material/Modal";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/base/ClickAwayListener";
import StorageIcon from "@mui/icons-material/Storage";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import AddToQueueIcon from "@mui/icons-material/AddToQueue";
import { IGroup } from "@api/Groups.api";
import InputComponent from "@ui/components/Input/Input.component";
import useDictionary from "@ui/hooks/useDictionary";
import ListSkeletonComponent from "./List-skeleton.component";
import GroupItemComponent from "./Group-item.component";
import AddGroupContainer from "../Add-group/Add-group.container";
import EditGroupContainer from "../Edit-group/Edit-group.container";
import GroupDeleteContainer from "../Group-delete/Group-delete.container";
import DeviceDeleteContainer from "../Device-delete/Device-delete.container";
import { IProps } from "./IProps";
import AddDeviceToGroupContainer from "../Add-device-to-group/Add-device-to-group.container";

const Component: React.FC<IProps> = ({
    addDialogOpen,
    tab,
    page,
    selectedDevice,
    editedDeviceImei,
    infoDeviceImei,
    addGroupVisible,
    defaultGroup,
    groups,
    devices,
    loading,
    visibleList,
    selectedGroupId,
    selectedDevices,
    selectedGroupDevices,
    addDeviceToGroupVisible,
    groupSettingsVisible,
    groupDeleteVisible,
    deviceDeleteVisible,
    deviceBtnHover,
    filter,
    userType,
    dateFilter,
    fromDateFilter,
    toDateFilter,
    settingDeviceImei,
    lang,
    columnVisibilityModel,
    zoom,
    sortModel,
    settingsList,
    speedDialHover,
    onGroupClick,
    onGroupSelect,
    onGroupAddDeviceOpen,
    onGroupAddDeviceClose,
    onGroupSettingsOpen,
    onGroupSettingsClose,
    onDeviceSelect,
    onDeviceClick,
    onAddGroupOpen,
    onAddGroupClose,
    onAddDeviceOpen,
    onGroupDeleteOpen,
    onGroupDeleteClose,
    onDeviceDeleteOpen,
    onDeviceDeleteClose,
    onDeviceDescriptionOpen,
    onDeviceTelemetryOpen,
    onDeviceBtnHoverStart,
    onDeviceBtnHoverStop,
    onSearchChange,
    onAddDialog,
    onAddDiologClose,
    onDeviceSettingsOpen,
    onAddDeviceInDbOpen,
    onDeviceInfoOpen,
    onSpeedDialHoverStart,
    onSpeedDialHoverStop,
}: IProps) => {
    const scrollRef = useRef<HTMLUListElement>(null);
    const getWord = useDictionary();
    const popperClass = speedDialHover
        ? "hidden"
        : "groups__menu-tooltip-popper";
    const checkGroupMatch = (group: IGroup) => {
        if (!filter) {
            return true;
        }
        const { id } = group;
        const deviceList = devices.filter((device) => device.groupId === id);
        const regExp = new RegExp(filter, "i");
        const isSomeDeviceMatch = deviceList.some((device) => {
            const { imei, name } = device;
            return regExp.test(name) || regExp.test(imei);
        });
        return isSomeDeviceMatch;
    };
    const getGroupItem = (group: IGroup) => {
        const { id, name, is_default } = group;
        const visible = visibleList[id];
        let deviceList = devices.filter((device) => device.groupId === id);
        if (filter) {
            const regExp = new RegExp(filter, "i");
            deviceList = deviceList.filter((device) => {
                const { imei, name } = device;
                return regExp.test(name) || regExp.test(imei);
            });
        }
            return (
                <GroupItemComponent
                    tab={tab}
                    page={page}
                    name={name}
                    devices={deviceList}
                    loading={loading}
                    key={id}
                    id={id}
                    scrollRef={scrollRef}
                    visible={visible}
                    isGroupDefault={is_default}
                    defaultGroup={defaultGroup}
                    selectedGroupId={selectedGroupId}
                    selectedDevices={selectedDevices}
                    selectedGroupDevices={selectedGroupDevices}
                    selectedDevice={selectedDevice}
                    editedDeviceImei={editedDeviceImei}
                    infoDeviceImei={infoDeviceImei}
                    deviceBtnHover={deviceBtnHover}
                    settingDeviceImei={settingDeviceImei}
                    lang={lang}
                    columnVisibilityModel={columnVisibilityModel}
                    dateFilter={dateFilter}
                    fromDateFilter={fromDateFilter}
                    toDateFilter={toDateFilter}
                    zoom={zoom}
                    sortModel={sortModel}
                    settingsList={settingsList}
                    onGroupClick={onGroupClick}
                    onGroupAddDeviceOpen={onGroupAddDeviceOpen}
                    onGroupSettingsOpen={onGroupSettingsOpen}
                    onGroupDeleteOpen={onGroupDeleteOpen}
                    onDeviceSelect={onDeviceSelect}
                    onDeviceClick={onDeviceClick}
                    onGroupSelect={onGroupSelect}
                    onDeviceDeleteOpen={onDeviceDeleteOpen}
                    onDeviceDescriptionOpen={onDeviceDescriptionOpen}
                    onDeviceTelemetryOpen={onDeviceTelemetryOpen}
                    onDeviceBtnHoverStart={onDeviceBtnHoverStart}
                    onDeviceBtnHoverStop={onDeviceBtnHoverStop}
                    onDeviceSettingsOpen={onDeviceSettingsOpen}
                    onDeviceInfoOpen={onDeviceInfoOpen}
                />
            );
    };
    const getDeviceCreateBtn = () => {
        if (userType === "superuser") {
            return (
                <SpeedDialAction
                    icon={<StorageIcon />}
                    tooltipTitle={getWord("addDeviceInDb")}
                    FabProps={{
                        tabIndex: 0,
                    }}
                    onClick={onAddDeviceInDbOpen}
                    onTouchStartCapture={onSpeedDialHoverStart}
                    onTouchEndCapture={onSpeedDialHoverStop}
                    onMouseOver={onSpeedDialHoverStart}
                    onMouseOut={onSpeedDialHoverStop}
                    onMouseEnter={onSpeedDialHoverStart}
                    onFocus={onSpeedDialHoverStart}
                    onMouseLeave={onSpeedDialHoverStop}
                    onBlur={onSpeedDialHoverStop}
                />
            );
        }
    };
    const getAddDeviceBtn = () => {
        if (userType === "superuser" || userType === "regular") {
            return (
                <SpeedDialAction
                    icon={<AddToQueueIcon />}
                    tooltipTitle={getWord("addDevice")}
                    FabProps={{
                        tabIndex: 0,
                    }}
                    onClick={onAddDeviceOpen}
                    onTouchStartCapture={onSpeedDialHoverStart}
                    onTouchEndCapture={onSpeedDialHoverStop}
                    onMouseOver={onSpeedDialHoverStart}
                    onMouseOut={onSpeedDialHoverStop}
                    onMouseEnter={onSpeedDialHoverStart}
                    onFocus={onSpeedDialHoverStart}
                    onMouseLeave={onSpeedDialHoverStop}
                    onBlur={onSpeedDialHoverStop}
                />
            );
        }
    };
    const createDeviceBtn = getDeviceCreateBtn();
    const addDeviceBtn = getAddDeviceBtn();

    const groupsList = groups.filter(checkGroupMatch).map(getGroupItem);
    const skeleton = new Array(5)
        .fill(0)
        .map((v, i) => <ListSkeletonComponent key={i} />);
    const items = groups.length === 0 && loading ? skeleton : groupsList;

    return (
        <>
            <Box className="groups" component="section">
                <InputComponent
                    lang={lang}
                    value={filter}
                    placeholder="inputNameOrImei"
                    isSearch
                    onChange={onSearchChange}
                />
                <List ref={scrollRef} className="groups__list">{items}</List>
                <Box className="groups__menu">
                    <ClickAwayListener onClickAway={onAddDiologClose}>
                        <Tooltip
                            classes={{
                                popper: popperClass,
                                tooltip: "groups__menu-tooltip",
                            }}
                            title={getWord("addObject")}
                            placement="top"
                        >
                            <SpeedDial
                                classes={{
                                    root: "groups__menu-btn-wrap",
                                    fab: "groups__menu-btn",
                                    actions: "groups__menu-actions",
                                }}
                                direction="left"
                                ariaLabel="Add button"
                                icon={<SpeedDialIcon />}
                                onClick={onAddDialog}
                                open={addDialogOpen}
                            >
                                {createDeviceBtn}
                                {addDeviceBtn}
                                <SpeedDialAction
                                    icon={<DevicesOtherIcon />}
                                    tooltipTitle={getWord("addGroup")}
                                    FabProps={{
                                        tabIndex: 0,
                                    }}
                                    onClick={onAddGroupOpen}
                                    onTouchStartCapture={onSpeedDialHoverStart}
                                    onTouchEndCapture={onSpeedDialHoverStop}
                                    onMouseOver={onSpeedDialHoverStart}
                                    onMouseOut={onSpeedDialHoverStop}
                                    onMouseEnter={onSpeedDialHoverStart}
                                    onFocus={onSpeedDialHoverStart}
                                    onMouseLeave={onSpeedDialHoverStop}
                                    onBlur={onSpeedDialHoverStop}
                                />
                            </SpeedDial>
                        </Tooltip>
                    </ClickAwayListener>
                </Box>
            </Box>
            <Modal open={addGroupVisible} onClose={onAddGroupClose}>
                <AddGroupContainer onClose={onAddGroupClose} />
            </Modal>
            <Modal open={groupSettingsVisible} onClose={onGroupSettingsClose}>
                <EditGroupContainer onClose={onGroupSettingsClose} />
            </Modal>
            <Modal
                open={addDeviceToGroupVisible}
                onClose={onGroupAddDeviceClose}
            >
                <AddDeviceToGroupContainer onClose={onGroupAddDeviceClose} />
            </Modal>
            <Modal open={groupDeleteVisible} onClose={onGroupDeleteClose}>
                <GroupDeleteContainer onClose={onGroupDeleteClose} />
            </Modal>
            <Modal open={deviceDeleteVisible} onClose={onDeviceDeleteClose}>
                <DeviceDeleteContainer onClose={onDeviceDeleteClose} />
            </Modal>
        </>
    );
};

const GroupsComponent = React.memo(Component);

export default GroupsComponent;
